import React, { useState, useEffect } from 'react';
import Wrapper from '../../Main/Universal/Wrapper';
import { postAPI, postAPIForm, formatDate2, formatDate } from '../../../app/api';
import Swal from 'sweetalert2';
import editIcon from "../../../theme/assets/images/icon/edit-icon.svg"
import deleteIcon from "../../../theme/assets/images/icon/delete-icon.svg"
import memberIcon from "../../../theme/assets/images/icon/member-icon.svg"

export default function ManageMembers() {
    const [members, setMembers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [bloodGroupFilter, setBloodGroupFilter] = useState("");
    const [recentlyJoinedFilter, setRecentlyJoinedFilter] = useState("");
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewMember, setViewMember] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [selectedMember, setSelectedMember] = useState(null);
    const [selectedImageFile, setSelectedImageFile] = useState(null);
    const [selectedFormFile, setSelectedFormFile] = useState(null);

    const [formData, setFormData] = useState({
        first_name: '',
        middle_name: '',
        last_name: '',
        designation: '',
        dob_of_spouse: '',
        email_id: '',
        mobile_number: '',
        landline_number: '',
        passing_year: '',
        discipline: '',
        blood_group: '',
        postal_address: '',
        pin_code: '',
        state: '',
        country: '',
        spouse_name: '',
        date_of_birth: '',
        introduce_by: '',
        introduce_passing_year: '',
        introduce_discipline: '',
        image: '',
        is_agree: true,
        action: 'ADD',
        offline_form: '',
        payment_method: 'CASH',
        transaction_reference: '',
        added_by_admin: true
    });

    const [paymentData, setPaymentData] = useState({
        membership_type: 'annual',
        membership_amount: 10000,
        payment_mode: 'CASH',
        invoice_number: '',
        receipt_number: '',
        submission_status: 'PAID'
    });

    // Fetch all members
    useEffect(() => {
        fetchMembers();
    }, []);

    // Filter members by search term, blood group, and recently joined
    const filteredMembers = members.filter(member => {
        const term = searchTerm.toLowerCase();
        const matchesSearch = (
            (member.first_name && member.first_name.toLowerCase().includes(term)) ||
            (member.middle_name && member.middle_name.toLowerCase().includes(term)) ||
            (member.last_name && member.last_name.toLowerCase().includes(term)) ||
            (member.email_id && member.email_id.toLowerCase().includes(term)) ||
            (member.mobile_number && member.mobile_number.toLowerCase().includes(term))
        );

        const matchesBloodGroup = !bloodGroupFilter || member.blood_group === bloodGroupFilter;

        // Filter by recently joined using member_id format: YYYYMMDD_XXX
        let matchesRecentlyJoined = true;
        if (recentlyJoinedFilter && member.member_id) {
            // Parse member_id: first 4 chars = year, next 2 = month, next 2 = day
            const memberId = member.member_id;
            const year = parseInt(memberId.substring(0, 4));
            const month = parseInt(memberId.substring(4, 6)) - 1; // JS months are 0-indexed
            const day = parseInt(memberId.substring(6, 8));

            const memberJoinDate = new Date(year, month, day);
            const now = new Date();
            const monthsAgo = new Date();
            monthsAgo.setMonth(now.getMonth() - parseInt(recentlyJoinedFilter));

            matchesRecentlyJoined = memberJoinDate >= monthsAgo;
        } else if (recentlyJoinedFilter && !member.member_id) {
            // If filter is set but member has no member_id, exclude them
            matchesRecentlyJoined = false;
        }

        return matchesSearch && matchesBloodGroup && matchesRecentlyJoined;
    });

    const fetchMembers = async () => {
        try {
            setLoading(true);
            // const response = await postAPI('authentication/users/list', {});
            const response = await postAPI('finance/list', {});
            if (response?.status === 1) {
                setMembers(response?.data || []);
            }
        } catch (error) {
            console.error('Error fetching members:', error);
            Swal.fire('Error', 'Failed to fetch members', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle form changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle image file selection
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedImageFile(file);
            // Create preview URL
            setFormData(prev => ({
                ...prev,
                image: URL.createObjectURL(file)
            }));
        }
    };

    // Handle form file upload
    const handleFormFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFormFile(file);
            setFormData(prev => ({
                ...prev,
                offline_form: file.name
            }));
        }
    };

    // Handle payment form changes
    const handlePaymentChange = (e) => {
        const { name, value } = e.target;
        setPaymentData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Generate unique invoice number
    // const generateInvoiceNumber = () => {
    //     const date = new Date();
    //     const year = date.getFullYear().toString().slice(2);
    //     const month = String(date.getMonth() + 1).padStart(2, '0');
    //     const day = String(date.getDate()).padStart(2, '0');
    //     const random = Math.floor(1000 + Math.random() * 9000);
    //     return `INV${year}${month}${day}${random}`;
    // };

    const generateUniqueId = (prefix, user_code) => {
        let ISO = new Date().toISOString().split('T')[0]
        let serial_no = Math.floor(1000 + Math.random() * 9000); // 4 digit random number
        let timestamp = ISO.split('-')
        let date = timestamp[2]
        let month = parseInt(timestamp[1])
        let year = timestamp[0].slice(2)
        // Combine prefix, timestamp, and random number to create the unique ID
        const userId = `${prefix}${user_code}${date}${String.fromCharCode(64 + month)}${serial_no}${year}`;

        return userId;
    }

    // Open payment form for member
    const handleOpenPaymentForm = (member) => {
        setSelectedMember(member);
        setPaymentData({
            membership_type: 'annual',
            membership_amount: 10000,
            payment_mode: 'CASH',
            invoice_number: generateUniqueId('I', member.user_code),
            receipt_number: '',
            submission_status: 'PAID'
        });
        setShowPaymentForm(true);
    };

    // Submit payment
    const handleSubmitPayment = async (e) => {
        e.preventDefault();
        try {
            if (!paymentData.invoice_number || !paymentData.membership_amount) {
                Swal.fire('Validation', 'Please fill all required fields', 'warning');
                return;
            }

            // Calculate membership dates
            const startDate = new Date();
            let endDate = new Date();

            if (paymentData.membership_type === 'lifetime') {
                endDate.setFullYear(endDate.getFullYear() + 100); // Life membership
            } else {
                endDate.setFullYear(endDate.getFullYear() + 1); // Annual membership
            }

            // Create transaction
            const transactionPayload = {
                module: 'MEMBERSHIP',
                user_code: selectedMember.user_code,
                invoice_number: paymentData.invoice_number,
                invoice_no: paymentData.invoice_number,
                receipt_number: paymentData.receipt_number || paymentData.invoice_number,
                currency: 'INR',
                amount: paymentData.membership_amount,
                membership_amount: paymentData.membership_amount,
                payment_amount: paymentData.membership_amount,
                gateway: paymentData.payment_mode === 'CASH' ? 'CASH' : paymentData.payment_mode === 'CHECK' ? 'CHECK' : 'ONLINE',
                mode: paymentData.payment_mode,
                submission_status: paymentData.submission_status,
                payment_payload: {
                    member_name: `${selectedMember.first_name} ${selectedMember.last_name}`,
                    email: selectedMember.email_id,
                    phone: selectedMember.mobile_number,
                    membership_type: paymentData.membership_type
                },
                orderData: {
                    reference_number: paymentData.reference_number
                }
            };

            const transactionResponse = await postAPI('finance/create', transactionPayload);

            if (transactionResponse?.status === 1) {
                Swal.fire('Success', 'Payment recorded successfully', 'success');
                setShowPaymentForm(false);
                setSelectedMember(null);
                fetchMembers();
            } else {
                Swal.fire('Error', transactionResponse?.msg || 'Failed to create transaction', 'error');
            }
        } catch (error) {
            console.error('Error processing payment:', error);
            Swal.fire('Error', error.message || 'Failed to process payment', 'error');
        }
    };

    // Add/Update member
    const handleAddMember = async (e) => {
        e.preventDefault();
        try {
            // Enhanced validation for complete form
            if (!formData.first_name || !formData.last_name || !formData.email_id || !formData.mobile_number ||
                !formData.passing_year || !formData.discipline || !formData.blood_group || !formData.date_of_birth ||
                !formData.introduce_by || !formData.introduce_passing_year || !formData.introduce_discipline) {
                Swal.fire('Validation', 'Please fill all required fields', 'warning');
                return;
            }

            // Validate payment type and cheque number
            if (formData.payment_type === 'Cheque' && !formData.cheque_number) {
                Swal.fire('Validation', 'Cheque number is required when payment type is Cheque', 'warning');
                return;
            }

            // Add registration date fields (like UI form)
            const now = new Date();
            const registration_month = now.toLocaleString('default', { month: 'long' });
            const registration_date = now.getDate();
            const registration_year = now.getFullYear();

            const submitData = {
                first_name: formData.first_name,
                middle_name: formData.middle_name,
                last_name: formData.last_name,
                designation: formData.designation,
                dob_of_spouse: formData.dob_of_spouse,
                email_id: formData.email_id,
                mobile_number: formData.mobile_number,
                landline_number: formData.landline_number,
                passing_year: formData.passing_year,
                discipline: formData.discipline,
                blood_group: formData.blood_group,
                postal_address: formData.postal_address,
                pin_code: formData.pin_code,
                state: formData.state,
                country: formData.country,
                spouse_name: formData.spouse_name,
                date_of_birth: formData.date_of_birth,
                introduce_by: formData.introduce_by,
                introduce_passing_year: formData.introduce_passing_year,
                introduce_discipline: formData.introduce_discipline,
                image: formData.image,
                is_agree: formData.is_agree,
                action: editingId ? 'UPDATE' : 'ADD',
                registration_month,
                registration_date,
                registration_year,
                // Admin-added member fields
                offline_form: formData.offline_form,
                payment_method: formData.payment_method,
                added_by_admin: true
            };

            // Upload image if a new file is selected
            if (selectedImageFile) {
                const imgFormData = new FormData();
                imgFormData.append('baseDir', 'members');
                imgFormData.append('filename', 'member_image');
                imgFormData.append('original_file', selectedImageFile);

                const imgResponse = await postAPIForm('file/uploadFile', imgFormData);
                if (imgResponse?.status === 1) {
                    submitData.image = imgResponse?.filepath;
                } else {
                    console.error('Image upload failed:', imgResponse);
                }
            }

            // Upload offline form if selected
            if (selectedFormFile) {
                const formFileData = new FormData();
                formFileData.append('baseDir', 'offline_forms');
                formFileData.append('filename', 'offline_form');
                formFileData.append('original_file', selectedFormFile);

                const formResponse = await postAPIForm('file/uploadFile', formFileData);
                if (formResponse?.status === 1) {
                    submitData.offline_form = formResponse?.filepath;
                } else {
                    console.error('Form file upload failed:', formResponse);
                }
            }

            let response;
            if (editingId) {
                submitData.id = editingId;
                response = await postAPI('authentication/users/update-profile', submitData);
            } else {
                // Use the same API call as working UI form
                response = await postAPI('authentication/users/add-member', submitData);
            }

            if (response?.status === 1) {
                Swal.fire('Success', response?.msg || 'Member saved successfully', 'success');
                resetForm();
                fetchMembers();
            } else {
                Swal.fire('Error', response?.msg || 'Failed to save member', 'error');
            }
        } catch (error) {
            console.error('Error saving member:', error);
            Swal.fire('Error', error.message || 'Failed to save member', 'error');
        }
    };

    // Reset form function
    const resetForm = () => {
        setFormData({
            first_name: '',
            middle_name: '',
            last_name: '',
            designation: '',
            dob_of_spouse: '',
            email_id: '',
            mobile_number: '',
            landline_number: '',
            passing_year: '',
            discipline: '',
            blood_group: '',
            postal_address: '',
            pin_code: '',
            state: '',
            country: '',
            spouse_name: '',
            date_of_birth: '',
            introduce_by: '',
            introduce_passing_year: '',
            introduce_discipline: '',
            image: '',
            is_agree: true,
            action: 'ADD',
            offline_form: '',
            payment_method: 'CASH',
            transaction_reference: '',
            added_by_admin: true
        });
        setShowForm(false);
        setEditingId(null);
        setSelectedImageFile(null);
        setSelectedFormFile(null);
    };

    // Delete member
    const handleApproveMember = async (member) => {
        const result = await Swal.fire({
            title: 'Confirm Approve',
            text: 'Are you sure you want to approve this member?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Approve',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const response = await postAPI('authentication/users/approve', { user_code: member.user_code, approved_by: 'admin' });
                if (response?.status === 1) {
                    Swal.fire('Success', 'Member approved successfully', 'success');
                    fetchMembers();
                }
            } catch (error) {
                Swal.fire('Error', 'Failed to approve member', 'error');
            }
        }
    };

    // Delete member
    const handleDeleteMember = async (id) => {
        const result = await Swal.fire({
            title: 'Confirm Delete',
            text: 'Are you sure you want to delete this member?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const response = await postAPI('authentication/users/delete', { id });
                if (response?.status === 1) {
                    Swal.fire('Success', 'Member deleted successfully', 'success');
                    fetchMembers();
                } else {
                    Swal.fire('Error', response?.msg || 'Failed to delete member', 'error');
                }
            } catch (error) {
                Swal.fire('Error', 'Failed to delete member', 'error');
            }
        }
    };

    // Toggle member status (Active/Inactive)
    const handleToggleMemberStatus = async (member) => {
        const newStatus = member.status === 1 ? 0 : 1;
        const statusText = newStatus === 1 ? 'Active' : 'Inactive';
        const action = newStatus === 1 ? 'activate' : 'deactivate';

        const result = await Swal.fire({
            title: `Confirm ${statusText}`,
            text: `Are you sure you want to ${action} ${member.first_name} ${member.last_name}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `${action.charAt(0).toUpperCase() + action.slice(1)}`,
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const response = await postAPI('finance/update-status', {
                    id: member.id,
                    status: newStatus
                });
                if (response?.status === 1) {
                    const actionText = newStatus === 1 ? 'activated' : 'inactivated';
                    Swal.fire('Success', `Member ${actionText} successfully`, 'success');
                    fetchMembers();
                } else {
                    Swal.fire('Error', response?.msg || `Failed to ${action} member`, 'error');
                }
            } catch (error) {
                Swal.fire('Error', `Failed to ${action} member`, 'error');
                console.error('Error updating member status:', error);
            }
        }
    };

    const handleToggleForm = () => {
        if (showForm) {
            // If form is shown, close it and reset
            setShowForm(false);
            resetForm();
        } else {
            // If form is hidden, show it with fresh data
            setEditingId(null);
            setFormData({
                first_name: '',
                middle_name: '',
                last_name: '',
                designation: '',
                dob_of_spouse: '',
                email_id: '',
                mobile_number: '',
                landline_number: '',
                passing_year: '',
                blood_group: '',
                discipline: '',
                postal_address: '',
                pin_code: '',
                state: '',
                country: '',
                spouse_name: '',
                date_of_birth: '',
                introduce_by: '',
                introduce_passing_year: '',
                introduce_discipline: '',
                image: '',
                is_agree: true,
                action: 'ADD',
                offline_form: '',
                payment_method: 'CASH',
                transaction_reference: '',
                added_by_admin: true
            });
            setShowForm(true);
        }
    };


    // Handle view member details
    const handleView = (member) => {
        setViewMember(member);
        setShowViewModal(true);
    };


    return (
        <>
            <Wrapper>
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <h3 className="mb-0">Member Management</h3>
                            <div className="d-flex align-items-center" style={{ gap: '1rem' }}>
                                <input
                                    type="text"
                                    className="form-control"
                                    style={{ minWidth: 220, maxWidth: 300 }}
                                    placeholder="Search members..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                />
                                <select
                                    className="form-control"
                                    style={{ minWidth: 150, maxWidth: 180 }}
                                    value={bloodGroupFilter}
                                    onChange={e => setBloodGroupFilter(e.target.value)}
                                >
                                    <option value="">All Blood Groups</option>
                                    <option value="O+">O+</option>
                                    <option value="O-">O-</option>
                                    <option value="A+">A+</option>
                                    <option value="A-">A-</option>
                                    <option value="B+">B+</option>
                                    <option value="B-">B-</option>
                                    <option value="AB+">AB+</option>
                                    <option value="AB-">AB-</option>
                                </select>
                                <select
                                    className="form-control"
                                    style={{ minWidth: 150, maxWidth: 180 }}
                                    value={recentlyJoinedFilter}
                                    onChange={e => setRecentlyJoinedFilter(e.target.value)}
                                >
                                    <option value="">Recently Joined</option>
                                    <option value="1">Last 1 Month</option>
                                    <option value="3">Last 3 Months</option>
                                    <option value="6">Last 6 Months</option>
                                    <option value="12">Last 1 Year</option>
                                </select>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleToggleForm}
                                >
                                    {showForm ? 'Cancel' : 'Add New Member'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Add/Update Member Modal */}
                <div className={`modal fade ${showForm ? "show d-block modal-white" : ""}`} tabIndex="-1" style={{ background: showForm ? "rgba(0,0,0,0.5)" : "transparent" }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{editingId ? "Edit Member" : "Add New Member"}</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={handleToggleForm}
                                ></button>
                            </div>

                            <div className="modal-body">
                                <form onSubmit={handleAddMember}>
                                    <div className="row">
                                        {/* Profile Image Upload */}
                                        <div className="col-12 mb-4">
                                            <label className="form-label">Profile Picture</label>
                                            <div className="d-flex align-items-center gap-3">
                                                <div style={{
                                                    width: 100,
                                                    height: 100,
                                                    borderRadius: '10%',
                                                    overflow: 'hidden',
                                                    background: '#f0f0f0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: '2px dashed #ccc'
                                                }}>
                                                    {formData.image ? (
                                                        <img
                                                            src={formData.image}
                                                            alt="Profile"
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        />
                                                    ) : (
                                                        <span style={{ color: '#999', fontSize: '12px', textAlign: 'center' }}>No Image</span>
                                                    )}
                                                </div>
                                                <div>
                                                    <input
                                                        type="file"
                                                        className="form-control"
                                                        accept="image/*"
                                                        onChange={handleImageChange}
                                                    />
                                                    <small className="text-muted">Select a new image to update profile picture</small>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">First Name *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="first_name"
                                                value={formData.first_name}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Middle Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="middle_name"
                                                value={formData.middle_name}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Last Name *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="last_name"
                                                value={formData.last_name}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Email ID *</label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                name="email_id"
                                                value={formData.email_id}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Mobile Number *</label>
                                            <input
                                                type="tel"
                                                className="form-control"
                                                name="mobile_number"
                                                value={formData.mobile_number}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Landline Number</label>
                                            <input
                                                type="tel"
                                                className="form-control"
                                                name="landline_number"
                                                value={formData.landline_number}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Date of Birth *</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="date_of_birth"
                                                value={formData.date_of_birth}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Discipline *</label>
                                            <select
                                                className="form-control"
                                                name="discipline"
                                                value={formData.discipline}
                                                onChange={handleChange}
                                            >
                                                <option value="">Select discipline</option>
                                                <option value="Architecture">Architecture</option>
                                                <option value="Civil">Civil</option>
                                                <option value="Electrical">Electrical</option>
                                                <option value="Mechanical">Mechanical</option>
                                                <option value="Metallurgy">Metallurgy</option>
                                                <option value="Mining">Mining</option>
                                            </select>
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Blood Group *</label>
                                            <select
                                                className="form-control"
                                                name="blood_group"
                                                value={formData.blood_group}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">Select blood group</option>
                                                <option value="O+">O+</option>
                                                <option value="O-">O-</option>
                                                <option value="A+">A+</option>
                                                <option value="A-">A-</option>
                                                <option value="B+">B+</option>
                                                <option value="B-">B-</option>
                                                <option value="AB+">AB+</option>
                                                <option value="AB-">AB-</option>
                                            </select>
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Year of Passing *</label>
                                            <select
                                                className="form-control"
                                                name="passing_year"
                                                value={formData.passing_year}
                                                onChange={handleChange}
                                            >
                                                <option value="">Select year</option>
                                                {Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i).map(year => (
                                                    <option key={year} value={year}>{year}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Designation</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="designation"
                                                value={formData.designation}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Status</label>
                                            <select
                                                className="form-control"
                                                name="status"
                                                value={formData.status}
                                                onChange={handleChange}
                                            >
                                                <option value={1}>Active</option>
                                                <option value={0}>Inactive</option>
                                            </select>
                                        </div>

                                        <div className="col-md-12 mb-3">
                                            <label className="form-label">Postal Address</label>
                                            <textarea
                                                className="form-control"
                                                name="postal_address"
                                                value={formData.postal_address}
                                                onChange={handleChange}
                                                rows="2"
                                            ></textarea>
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">State</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="state"
                                                value={formData.state}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Country</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="country"
                                                value={formData.country}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Pin Code</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="pin_code"
                                                value={formData.pin_code}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Spouse Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="spouse_name"
                                                value={formData.spouse_name}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Spouse Date of Birth</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="dob_of_spouse"
                                                value={formData.dob_of_spouse}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Introduce By *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="introduce_by"
                                                value={formData.introduce_by}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Introducer Passing Year *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="introduce_passing_year"
                                                value={formData.introduce_passing_year}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Introducer Discipline *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="introduce_discipline"
                                                value={formData.introduce_discipline}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>

                                    {/* Payment & Registration Information Section */}
                                    <div className="row mt-3">
                                        <div className="col-12 mb-2">
                                            <h6 className="text-primary border-bottom pb-2">Payment & Registration Information</h6>
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Payment Method *</label>
                                            <select
                                                className="form-control"
                                                name="payment_method"
                                                value={formData.payment_method}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="CASH">Cash</option>
                                                <option value="CHEQUE">Cheque</option>
                                                <option value="ONLINE_TRANSFER">Online Transfer</option>
                                                <option value="UPI">UPI</option>
                                            </select>
                                        </div>

                                        {(formData.payment_method === 'CHEQUE' || formData.payment_method === 'UPI' || formData.payment_method === 'ONLINE_TRANSFER') && (
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">
                                                    {formData.payment_method === 'CHEQUE' ? 'Cheque No.' : formData.payment_method === 'UPI' ? 'UPI Transaction No.' : 'Transaction Reference No.'}
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="transaction_reference"
                                                    value={formData.transaction_reference || ''}
                                                    onChange={handleChange}
                                                    placeholder={formData.payment_method === 'CHEQUE' ? 'Enter cheque number' : formData.payment_method === 'UPI' ? 'Enter UPI transaction ID' : 'Enter transaction reference'}
                                                />
                                            </div>
                                        )}

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Offline Registration Form (PDF only)</label>
                                            <input
                                                type="file"
                                                className="form-control"
                                                accept=".pdf"
                                                onChange={handleFormFileChange}
                                            />
                                            <small className="text-muted">
                                                Upload scanned offline registration form (PDF only)
                                                {formData.offline_form && ` - Selected: ${formData.offline_form}`}
                                            </small>
                                        </div>
                                    </div>

                                    <button type="submit" className="btn btn-success">
                                        {editingId ? "Update" : "Add"} Member
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Payment Modal */}
                <div className={`modal fade ${showPaymentForm ? "show d-block modal-white" : ""}`} tabIndex="-1" style={{ background: showPaymentForm ? "rgba(0,0,0,0.5)" : "transparent" }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Record Payment</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowPaymentForm(false)}
                                ></button>
                            </div>

                            <div className="modal-body">
                                {selectedMember && (
                                    <div className="mb-3">
                                        <strong>Member:</strong> {selectedMember.first_name} {selectedMember.last_name}
                                        <br />
                                        <strong>Email:</strong> {selectedMember.email_id}
                                    </div>
                                )}

                                <form onSubmit={handleSubmitPayment}>
                                    <div className="mb-3">
                                        <label className="form-label">Membership Type *</label>
                                        <select
                                            className="form-control"
                                            name="membership_type"
                                            value={paymentData.membership_type}
                                            onChange={handlePaymentChange}
                                            required
                                        >
                                            <option value="lifetime">Life Membership</option>
                                            <option value="annual">Annual Membership</option>
                                        </select>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Payment Mode *</label>
                                        <select
                                            className="form-control"
                                            name="payment_mode"
                                            value={paymentData.payment_mode}
                                            onChange={handlePaymentChange}
                                            required
                                        >
                                            <option value="CASH">Cash</option>
                                            <option value="CHECK">Check</option>
                                            {/* <option value="ONLINE">Online</option> */}
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            {paymentData.payment_mode == 'CASH' ? 'Memo No *' : 'Cheque Number *'}
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="reference_number"
                                            value={paymentData.reference_number}
                                            onChange={handlePaymentChange}
                                            required
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Membership Amount *</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            name="membership_amount"
                                            value={paymentData.membership_amount}
                                            onChange={handlePaymentChange}
                                            required
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Invoice Number *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="invoice_number"
                                            value={paymentData.invoice_number}
                                            // onChange={handlePaymentChange}
                                            required
                                            disabled
                                        />
                                    </div>

                                    {/* <div className="mb-3">
                                            <label className="form-label">Receipt Number</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="receipt_number"
                                                value={paymentData.receipt_number}
                                                onChange={handlePaymentChange}
                                                placeholder="Optional"
                                            />
                                        </div> */}

                                    <div className="mb-3">
                                        <label className="form-label">Payment Status *</label>
                                        <select
                                            className="form-control"
                                            name="submission_status"
                                            value={paymentData.submission_status}
                                            onChange={handlePaymentChange}
                                            required
                                        >
                                            <option value="PAID">Paid</option>
                                            <option value="PENDING">Pending</option>
                                            <option value="FAILED">Failed</option>
                                        </select>
                                    </div>
                                    <button type="submit" className="btn btn-success w-100">
                                        Record Payment
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table cus-table">
                                <thead className="table-dark">
                                    <tr>
                                        <th>Image</th>
                                        <th>Member ID</th>
                                        <th>Name</th>
                                        <th>Contact Details</th>
                                        <th>Membership</th>
                                        <th>Passing Year</th>
                                        <th>Discipline</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredMembers && filteredMembers.length > 0 ? (
                                        filteredMembers.map((member, index) => member.is_approved === 1 ? (
                                            <tr key={index}>
                                                <td>
                                                    {member.image ? (
                                                        <img src={member.image} alt="Profile" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: '10%' }} />
                                                    ) : (
                                                        <span style={{ width: 48, height: 48, display: 'inline-block', background: '#eee', borderRadius: '10%' }} />
                                                    )}
                                                </td>
                                                <td>{member.member_id || '-'}</td>
                                                <td>
                                                    {member.first_name} {member.middle_name} {member.last_name}
                                                    <br />
                                                    <small>{member.designation}</small>
                                                </td>
                                                <td>
                                                    {member.email_id}
                                                    <br />
                                                    <small>{member.mobile_number}</small>
                                                </td>
                                                <td>
                                                    {
                                                        // Always show Lifetime Membership, and show registration date below it from registration_month/date/year
                                                        <>
                                                            <span className="badge badge-success">Lifetime Membership</span>
                                                            <br />
                                                            <small>
                                                                Since {member.registration_month && member.registration_date && member.registration_year
                                                                    ? `${member.registration_month} ${member.registration_date}, ${member.registration_year}`
                                                                    : '-'}
                                                            </small>
                                                        </>
                                                    }
                                                </td>
                                                <td>{member.passing_year || '-'}</td>
                                                <td>{member.discipline || '-'}</td>
                                                <td>
                                                    <span className={`badge badge-${member.status === 1 ? 'success' : 'danger'}`}>
                                                        {member.status === 1 ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                {
                                                    // member?.user_type == 'ALUMNI' ?
                                                    <td>
                                                        <button
                                                            className="btn btn-sm btn-primary me-2 px-1 py-1 my-0 mx-0"
                                                            onClick={() => handleView(member)}
                                                            title="View Details"
                                                        >
                                                            <i className={'fa fa-eye'} alt="View Details" />
                                                        </button>
                                                        <button
                                                            className={`btn me-2 px-0 py-0 my-0 mx-0 ${member.status === 1 ? '' : ''}`}
                                                            onClick={() => handleToggleMemberStatus(member)}
                                                            title={member.status === 1 ? 'Deactivate Member' : 'Activate Member'}
                                                        >
                                                            {member.status === 1 ? <img src={memberIcon} /> : <img src={memberIcon} />}
                                                        </button>
                                                        <button
                                                            className="btn me-2 px-0 py-0 my-0 mx-0"
                                                            onClick={() => {
                                                                setFormData(member);
                                                                setEditingId(member.id);
                                                                setShowForm(true);
                                                            }}
                                                        >
                                                            <img src={editIcon} alt="Edit" />
                                                        </button>
                                                        <button
                                                            className="btn px-0 py-0 my-0 mx-0"
                                                            onClick={() => handleDeleteMember(member.id)}
                                                        >
                                                            <img src={deleteIcon} alt="Delete" />
                                                        </button>
                                                    </td>
                                                    // :
                                                    // <td>
                                                    //     <button
                                                    //         className="btn me-2 px-0 py-0 my-0 mx-0"
                                                    //         onClick={() => {
                                                    //             setFormData(member);
                                                    //             setEditingId(member.id);
                                                    //             setShowForm(true);
                                                    //         }}
                                                    //     >
                                                    //         <img src={editIcon} alt="Edit" />
                                                    //     </button>
                                                    // </td>
                                                }
                                            </tr>
                                        )
                                            : <></>
                                        )
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="text-center py-4">
                                                No members found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* View Member Details Modal */}
                <div className={`modal fade ${showViewModal ? "show d-block modal-white" : ""}`} tabIndex="-1" style={{ background: showViewModal ? "rgba(0,0,0,0.5)" : "transparent" }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Member Details</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowViewModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                {viewMember && (
                                    <div className="row">
                                        {/* Profile Picture Display */}
                                        <div className="col-12 mb-4 text-center">
                                            <div style={{
                                                width: 120,
                                                height: 120,
                                                borderRadius: '50%',
                                                overflow: 'hidden',
                                                background: '#f0f0f0',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: '3px solid #dee2e6'
                                            }}>
                                                {viewMember.image ? (
                                                    <img
                                                        src={viewMember.image}
                                                        alt="Profile"
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                ) : (
                                                    <span style={{ color: '#999', fontSize: '14px' }}>No Photo</span>
                                                )}
                                            </div>
                                            <h5 className="mt-2 mb-0">{viewMember.first_name} {viewMember.middle_name} {viewMember.last_name}</h5>
                                            <small className="text-muted">{viewMember.designation || ''}</small>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <h6 className="text-muted">Personal Information</h6>
                                            <table className="table table-bordered">
                                                <tbody>
                                                    <tr>
                                                        <th style={{ width: '40%' }}>Name</th>
                                                        <td>{viewMember.first_name} {viewMember.middle_name} {viewMember.last_name}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Email</th>
                                                        <td>{viewMember.email_id || '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Mobile</th>
                                                        <td>{viewMember.mobile_number || '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Date of Birth</th>
                                                        {
                                                            viewMember.date_of_birth != 'Invalid date' ?
                                                                <td>{viewMember.date_of_birth ? formatDate(viewMember.date_of_birth)?.month + ' ' + formatDate(viewMember.date_of_birth)?.day + ', ' + formatDate(viewMember.date_of_birth)?.year : '-'}</td>
                                                                :
                                                                <></>
                                                        }
                                                    </tr>
                                                    <tr>
                                                        <th>Designation</th>
                                                        <td>{viewMember.designation || '-'}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <h6 className="text-muted">Academic Information</h6>
                                            <table className="table table-bordered">
                                                <tbody>
                                                    <tr>
                                                        <th style={{ width: '40%' }}>Discipline</th>
                                                        <td>{viewMember.discipline || '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Passing Year</th>
                                                        <td>{viewMember.passing_year || '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Introduced By</th>
                                                        <td>{viewMember.introduce_by || '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Introducer Year</th>
                                                        <td>{viewMember.introduce_passing_year || '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Introducer Discipline</th>
                                                        <td>{viewMember.introduce_discipline || '-'}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="col-md-12 mb-3">
                                            <h6 className="text-muted">Address Information</h6>
                                            <table className="table table-bordered">
                                                <tbody>
                                                    <tr>
                                                        <th style={{ width: '20%' }}>Postal Address</th>
                                                        <td>{viewMember.postal_address || '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>State</th>
                                                        <td>{viewMember.state || '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Country</th>
                                                        <td>{viewMember.country || '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Pin Code</th>
                                                        <td>{viewMember.pin_code || '-'}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="col-md-12 mb-3">
                                            <h6 className="text-muted">Family Information</h6>
                                            <table className="table table-bordered">
                                                <tbody>
                                                    <tr>
                                                        <th style={{ width: '20%' }}>Spouse Name</th>
                                                        <td>{viewMember.spouse_name || '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Spouse Date of Birth</th>
                                                        {
                                                            viewMember.dob_of_spouse != 'Invalid date' ?
                                                                <td>{viewMember.dob_of_spouse ? formatDate(viewMember.dob_of_spouse)?.month + ' ' + formatDate(viewMember.dob_of_spouse)?.day + ', ' + formatDate(viewMember.dob_of_spouse)?.year : '-'}</td>
                                                                : <></>
                                                        }
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="col-md-12 mb-3">
                                            <h6 className="text-muted">Membership Information</h6>
                                            <table className="table table-bordered">
                                                <tbody>
                                                    <tr>
                                                        <th style={{ width: '20%' }}>Membership Type</th>
                                                        <td>
                                                            {viewMember.membership_type ? (
                                                                <span className={`badge badge-${viewMember.membership_type === 'lifetime' ? 'success' : 'primary'}`}>
                                                                    {viewMember.membership_type === 'lifetime' ? 'Lifetime Membership' : 'Annual Membership'}
                                                                </span>
                                                            ) : '-'}
                                                        </td>
                                                    </tr>
                                                    {viewMember.membership_start_date && (
                                                        <tr>
                                                            <th>Membership Period</th>
                                                            <td>
                                                                {formatDate2(viewMember.membership_start_date)?.month} {formatDate2(viewMember.membership_start_date)?.day}, {formatDate2(viewMember.membership_start_date)?.year}
                                                                {' to '}
                                                                {formatDate2(viewMember.membership_end_date)?.month} {formatDate2(viewMember.membership_end_date)?.day}, {formatDate2(viewMember.membership_end_date)?.year}
                                                            </td>
                                                        </tr>
                                                    )}
                                                    <tr>
                                                        <th>Status</th>
                                                        <td>
                                                            <span className={`badge badge-${viewMember.status === 1 ? 'success' : 'danger'}`}>
                                                                {viewMember.status === 1 ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowViewModal(false)}
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => {
                                        setShowViewModal(false);
                                        setFormData(viewMember);
                                        setEditingId(viewMember.id);
                                        setShowForm(true);
                                    }}
                                >
                                    Edit Member
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </Wrapper>
        </>
    );
}
